import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Card, CardActions, CardContent, Chip, Typography } from '@mui/material';

import { useCalories } from 'src/hooks/useCalories';
import { addOpacity, toTitleCase } from 'lib/helpers';
import { LoadingImage } from 'lib/LoadingImage';
import { ApplicationState } from 'store/store';
import {
    IEnrichedProduct,
    INutritionMenuEnrichment,
    INutritionMenuEnrichmentFields,
    IOptionModifierSettings,
    IProductModifierSettings,
    IProductTagValue,
    mapProductTags
} from './model/Menu';

const PREFIX = 'MenuProductDetails';

const classes = {
    title: `${PREFIX}title`,
    cardContent: `${PREFIX}cardContent`,
    description: `${PREFIX}description`,
    allergenTitle: `${PREFIX}allergenTitle`,
    allergenChip: `${PREFIX}allergenChip`,
    longDescription: `${PREFIX}longDescription`,
    tagsTitle: `${PREFIX}tagsTitle`,
    tagsChip: `${PREFIX}tagsChip`,
    showMore: `${PREFIX}showMore`,
    nutritionalTitle: `${PREFIX}nutritionalTitle`,
    nutritionalInfoRow: `${PREFIX}nutritionalInfoRow`,
    tagImageContainer: `${PREFIX}tagImageContainer`,
    tagImage: `${PREFIX}tagImage`
};

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    border: `1px solid ${addOpacity(theme.palette.text.primary, 0.08)}`,
    [`& .${classes.title}`]: {
        marginLeft: theme.spacing(2),
        flex: 1
    },
    [`& .${classes.cardContent}`]: {
        padding: theme.spacing(1)
    },

    [`& .${classes.description}`]: {
        marginTop: theme.spacing(0)
    },

    [`& .${classes.allergenTitle}`]: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(0.25),
        color: theme.palette.titleTextColour
    },

    [`& .${classes.allergenChip}`]: {
        margin: theme.spacing(0.25),
        backgroundColor: theme.palette.text.primary,
        color: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius ? (theme.shape.borderRadius as number) * 2 : 0,
        fontWeight: theme.typography.fontWeightBold
    },

    [`& .${classes.longDescription}`]: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(0.25)
    },

    [`& .${classes.tagsTitle}`]: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(0.25),
        fontWeight: 'bold',
        color: theme.palette.titleTextColour
    },

    [`& .${classes.tagsChip}`]: {
        margin: theme.spacing(0.5),
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.text.primary}`,
        borderRadius: theme.shape.borderRadius ? (theme.shape.borderRadius as number) * 2 : 0,
        fontWeight: theme.typography.fontWeightBold,
        height: '34px',
        padding: `0 ${theme.spacing(0.75)}`,
        '&:first-child': {
            marginLeft: 0
        }
    },

    [`& .${classes.showMore}`]: {
        display: 'inline',
        color: theme.palette.hint,
        cursor: 'pointer'
    },

    [`& .${classes.nutritionalTitle}`]: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(0.25),
        fontWeight: 'bold',
        color: theme.palette.titleTextColour
    },

    [`& .${classes.nutritionalInfoRow}`]: {
        '&:last-child': {
            borderBottom: 'none'
        },
        borderBottom: `1px solid ${addOpacity(theme.palette.text.primary, 0.08)}`,
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1, 0)
    },

    [`& .${classes.tagImageContainer}`]: {
        height: 22,
        width: 22
    },

    [`& .${classes.tagImage}`]: {
        width: '100%',
        height: '100%',
        objectFit: 'contain'
    }
}));

interface IMenuProductDetailsProps {
    item: IEnrichedProduct;
    productModifiers: IProductModifierSettings[];
    optionModifiers: IOptionModifierSettings[];
    disabledImage?: boolean;
    hasVisibleModifiers?: boolean;
    showModifiers?: boolean;
    formModifierProducts?: (string[] | string)[];
    formModifierOptions?: (string[] | string)[];
}

export const MenuProductDetails: React.FC<React.PropsWithChildren<IMenuProductDetailsProps>> = props => {
    const {
        item,
        disabledImage,
        hasVisibleModifiers,
        showModifiers,
        formModifierProducts,
        productModifiers,
        formModifierOptions,
        optionModifiers
    } = props;
    const { menu } = useSelector((state: ApplicationState) => state.menu);
    const { key: energyKey, getCaloriesByNutrition, getCaloriesString } = useCalories();
    const { t } = useTranslation();

    const formModifierProductsArray = React.useMemo(
        () =>
            formModifierProducts
                ?.reduce<string[]>((acc, item) => {
                    if (Array.isArray(item)) {
                        return [...acc, ...item];
                    }
                    return [...acc, item];
                }, [])
                .filter(item => !!item),
        [formModifierProducts]
    );

    const formModifierOptionsArray = React.useMemo(
        () =>
            formModifierOptions
                ?.reduce<string[]>((acc, item) => {
                    if (Array.isArray(item)) {
                        return [...acc, ...item];
                    }
                    return [...acc, item];
                }, [])
                .filter(item => !!item),
        [formModifierOptions]
    );

    const totalCalories = React.useMemo(() => {
        let sum = 0;
        const flattenedOptions = optionModifiers.flatMap(optionModifier => optionModifier.options);

        if (formModifierOptionsArray && formModifierOptionsArray.length > 0 && flattenedOptions.length > 0) {
            for (const modifierOption of formModifierOptionsArray) {
                const option = flattenedOptions.find(option => option.id === modifierOption);
                if (option && option.nutrition && option.nutrition.length > 0) {
                    const calories = Number(getCaloriesByNutrition(option.nutrition || [])) || 0;
                    sum += calories;
                }
            }
        }

        const flattenedProducts = productModifiers.flatMap(productModifier => productModifier.products);

        if (
            formModifierProductsArray &&
            formModifierProductsArray.length > 0 &&
            flattenedProducts.length > 0
        ) {
            for (const modifierProduct of formModifierProductsArray) {
                const product = flattenedProducts.find(product => product.id === modifierProduct);
                if (product && product.nutrition && product.nutrition.length > 0) {
                    const calories = Number(getCaloriesByNutrition(product.nutrition || [])) || 0;
                    sum += calories;
                }
            }
        }

        const itemCalories =
            item.nutrition && item.nutrition.length > 0
                ? Number(getCaloriesByNutrition(item.nutrition || [])) || 0
                : 0;

        return sum + itemCalories;
    }, [
        optionModifiers,
        formModifierOptionsArray,
        getCaloriesByNutrition,
        item.nutrition,
        productModifiers,
        formModifierProductsArray
    ]);

    const combinedAllergens = React.useMemo(() => {
        const itemAllergens = item?.allergens || [];
        const allergens = new Set(itemAllergens);

        const flattenedProducts = productModifiers.flatMap(productModifier => productModifier.products);

        if (
            formModifierProductsArray &&
            formModifierProductsArray.length > 0 &&
            flattenedProducts.length > 0
        ) {
            for (const modifierProduct of formModifierProductsArray) {
                const product = flattenedProducts.find(product => product.id === modifierProduct);
                if (product && product.allergens && product.allergens.length > 0) {
                    product.allergens.forEach(allergen => allergens.add(allergen));
                }
            }
        }
        return Array.from(allergens);
    }, [formModifierProductsArray, item?.allergens, productModifiers]);

    const combinedTags = React.useMemo(() => {
        const itemTags = item?.tags || [];
        const tags = [...itemTags];

        const flattenedProducts = productModifiers.flatMap(productModifier => productModifier.products);

        const menuOptions = menu?.options;

        if (
            formModifierProductsArray &&
            formModifierProductsArray.length > 0 &&
            flattenedProducts.length > 0 &&
            menuOptions
        ) {
            for (const modifierProduct of formModifierProductsArray) {
                const product = flattenedProducts.find(product => product.id === modifierProduct);
                if (product && product.tags && product.tags.length > 0) {
                    const mappedProduct = mapProductTags(product, menuOptions);
                    tags.push(...mappedProduct.tags);
                }
            }
        }

        const seen = new Set();
        const uniqueTags = tags.filter(obj => {
            const key = `${obj.id}-${obj.title}`;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
        return uniqueTags;
    }, [formModifierProductsArray, item?.tags, productModifiers, menu?.options]);

    const nutritionsMatchesWithKey = React.useMemo(() => {
        if (item.nutrition && item.nutrition.length > 0) {
            const result: INutritionMenuEnrichmentFields[] = [];
            item.nutrition
                .map(nutrition => ({
                    ...nutrition,
                    values: nutrition.values.filter(n => n.nutrient === energyKey)
                }))
                .forEach(nutrition => {
                    if (nutrition.values.length > 0) {
                        result.push(nutrition);
                    }
                });
            return result;
        }
        return [];
    }, [energyKey, item]);

    const nutritionHasValueNotMatchesWithKey = React.useCallback(() => {
        let found = false;
        if (item.nutrition && item.nutrition.length > 0) {
            item.nutrition.forEach(nutrition =>
                nutrition.values.some(n => {
                    if (n.nutrient !== energyKey) {
                        found = true;
                    }
                })
            );
        }
        return found;
    }, [item.nutrition, energyKey]);

    const hasMoreInfo = React.useMemo(
        () => item.nutrition.length > 0 || (!!item.tags && item.tags.length > 0) || !!item.longDescription,
        [item.longDescription, item.nutrition.length, item.tags]
    );

    const showButton = React.useMemo(
        () =>
            !nutritionHasValueNotMatchesWithKey() &&
            !!item.tags &&
            item.tags.length === 0 &&
            !!item.longDescription === false,
        [item.longDescription, item.tags, nutritionHasValueNotMatchesWithKey]
    );

    const [showMore, setShowMore] = React.useState(hasMoreInfo && !showModifiers);

    const handleShowMore = React.useCallback(() => setShowMore(!showMore), [showMore]);
    const allergenMap = React.useMemo(() => {
        const kvPairs: [string, string][] = [];
        for (const { id, title } of menu?.options?.allergens || []) {
            kvPairs.push([id, title]);
        }
        return new Map(kvPairs);
    }, [menu?.options?.allergens]);

    const renderAllergenChip = React.useCallback(
        (allergen: string, index: number) => (
            <Chip
                key={allergen + '-' + index}
                label={allergenMap.get(allergen) || toTitleCase(allergen)}
                className={classes.allergenChip}
            />
        ),
        [allergenMap]
    );

    const renderTagsChip = React.useCallback(
        (tag: IProductTagValue, index: number) => (
            <Box key={tag.id + '-' + index} display="flex" alignItems="center" className={classes.tagsChip}>
                {!!tag.imageUrl && (
                    <Box marginRight={0.75} className={classes.tagImageContainer}>
                        <img className={classes.tagImage} src={tag.imageUrl} alt="" />
                    </Box>
                )}
                <Typography>{tag.title}</Typography>
            </Box>
        ),
        []
    );

    const renderNutritionalInformationValue = React.useCallback(
        (nutritionalInfoValue: INutritionMenuEnrichment) => {
            const isEnergy = nutritionalInfoValue.nutrient === energyKey;
            if (isEnergy) {
                return (
                    <Box className={classes.nutritionalInfoRow} key={nutritionalInfoValue.nutrient}>
                        <Typography variant="subtitle1">{nutritionalInfoValue.nutrient}</Typography>
                        <Typography variant="subtitle1">{getCaloriesString(totalCalories)}</Typography>
                    </Box>
                );
            }
            return (
                <Box className={classes.nutritionalInfoRow} key={nutritionalInfoValue.nutrient}>
                    <Typography variant="subtitle1">{nutritionalInfoValue.nutrient}</Typography>
                    <Typography variant="subtitle1">{nutritionalInfoValue.value}</Typography>
                </Box>
            );
        },
        [energyKey, getCaloriesString, totalCalories]
    );

    const nutritionHasMatchesWithKey = React.useCallback(
        (values: INutritionMenuEnrichment[]) => values.find(n => n.nutrient === energyKey),
        [energyKey]
    );

    const renderNutritions = React.useCallback(
        (nutrition: INutritionMenuEnrichmentFields, index: number) => (
            <Box key={nutrition.title + '-' + index}>
                <Typography variant="subtitle1" className={classes.nutritionalTitle}>
                    {nutrition.title}
                </Typography>
                {!!nutritionHasMatchesWithKey(nutrition.values) && (
                    <Typography variant="body1" color="textPrimary">
                        {t('ORDER_CALORIES_ADVISORY_MESSAGE')}
                    </Typography>
                )}

                {nutrition.values.map(renderNutritionalInformationValue)}
            </Box>
        ),
        [nutritionHasMatchesWithKey, renderNutritionalInformationValue, t]
    );
    const isImage = !!item.imageUrl;

    const isProductHasContent = React.useMemo(
        () =>
            !!item.shortDescription ||
            (!disabledImage && isImage) ||
            !!item.allergens ||
            (!hasVisibleModifiers && hasMoreInfo),
        [disabledImage, hasMoreInfo, hasVisibleModifiers, isImage, item.allergens, item.shortDescription]
    );

    if (!isProductHasContent) {
        return null;
    }
    return (
        <StyledCard elevation={0}>
            {!disabledImage && isImage && <LoadingImage src={item.imageUrl || ''} height={22.5} />}
            <CardContent className={classes.cardContent}>
                {!!item.shortDescription && (
                    <Typography variant="subtitle1" className={classes.description}>
                        {item.shortDescription}
                    </Typography>
                )}
                {!!combinedAllergens && combinedAllergens.length > 0 && (
                    <React.Fragment>
                        <Typography variant="subtitle1" className={classes.allergenTitle}>
                            <b>{t('PRODUCT_DETAILS_ALLERGENS_LABEL')}</b>
                        </Typography>
                        <Box display="flex" flexDirection="row" flexWrap="wrap">
                            {combinedAllergens.map(renderAllergenChip)}
                        </Box>
                    </React.Fragment>
                )}

                {showMore && !!combinedTags && combinedTags.length > 0 && (
                    <React.Fragment>
                        <Typography variant="subtitle1" className={classes.tagsTitle}>
                            {t('PREORDER_TAGS')}
                        </Typography>
                        <Box display="flex" flexDirection="row" flexWrap="wrap">
                            {combinedTags.map(renderTagsChip)}
                        </Box>
                    </React.Fragment>
                )}

                {!showMore && <Box>{nutritionsMatchesWithKey.map(renderNutritions)}</Box>}

                {showMore && item.nutrition.length > 0 && <Box>{item.nutrition.map(renderNutritions)}</Box>}

                {showMore && !!item.longDescription && (
                    <Typography variant="subtitle1" className={classes.longDescription}>
                        {item.longDescription}
                    </Typography>
                )}

                {hasMoreInfo && showModifiers && !showButton && (
                    <CardActions style={{ flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="subtitle2" onClick={handleShowMore} className={classes.showMore}>
                            {showMore ? t('COLLAPSE_INFO_BUTTON') : t('PREORDER_MORE_INFORMATION')}
                        </Typography>
                    </CardActions>
                )}
            </CardContent>
        </StyledCard>
    );
};
